import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, SimpleChanges, OnDestroy } from "@angular/core";
import { defaultLeafletLatitude, defaultLeafletLongitude, defaultLeafLetMapZoomValue, getSatelliteTileLayer, getTileLayer, leaflefApiKey, LoggedInUserModel, ResponseMessageTypes, RxjsService, SnackbarService } from "@app/shared";
import { IpolyLayersObject } from "@modules/event-management/components/dispatcher-map-view/dispatcher-map-view.component";
import { Control } from 'leaflet';
import "leaflet-draw";
import 'leaflet-routing-machine';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import 'leaflet.markercluster';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from "rxjs/operators";
declare var L: any;

const PREFIX_IMAGE_RELATIVE_PATH = '../../../../assets/img/dispatcher-map-images/';

enum AlarmCaptionPriority {
    RED = 1,
    BLUE = 2,
    YELLOW = 3,
    BLACK = 4
}
@Component({
    selector: "app-event-mgmt-leaf-let-new",
    templateUrl: "./event-mgmt-leaf-let.component.html",
    styleUrls: ["./event-mgmt-leaf-let.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventsMgmtSharedLeafLetNewComponent implements OnInit, OnDestroy {
    @Input() polyLayersObject: IpolyLayersObject;
    @Input() id = "dispatcher_map_view";
    @Input() caption = "";
    @Input() shouldShowLegend = true;
    @Input() boundaryRequestDetails;
    @Input() zoomInOptions = { latitude: defaultLeafletLatitude, longitude: defaultLeafletLongitude };
    @Input() zoomInOptionsForDispatcherArea = { southWestLatitude: 0, southWestLongitude: 0, northEastLatitude: 0, northEastLongitude: 0 };
    @Input() targetLatLong;
    @Input() isSignalRData: boolean = true;
    @Output() onSelection = new EventEmitter();
    @Output() onOpenPopup = new EventEmitter();
    @Output() afterDataRenderedInMapAction = new EventEmitter();

    container;
    map: L.Map;
    drawControl: Control;
    openedPopup: L.Popup;
    selectedLayer;
    loggedInUserData: LoggedInUserModel;
    encodedToken = "";
    editablePolygonLayers = new L.FeatureGroup();
    editableMarkerVehicleLayers = new L.FeatureGroup();
    editableMarkerSiteLayers = new L.FeatureGroup();
    currentLocationAndDestinationMarkerSiteLayers = new L.FeatureGroup();
    editablePolyLineLayers = new L.FeatureGroup();
    routingPathControl;
    sourceLatLong: L.LatLng;

    // Performance optimization properties
    private vehicleClusterGroup: any;
    private siteClusterGroup: any;
    private dataUpdateSubject = new Subject<void>();
    private destroy$ = new Subject<void>();
    private isRendering = false;
    private renderQueue: any[] = [];
    private readonly RENDER_DELAY = 16; // ~60fps
    private layerCache = new Map<string, L.Layer>();
    private clusteringEnabled = false; // Disabled by default for individual markers

    // Rendering synchronization properties
    private pendingRenderOperations = 0;
    private renderCompletionSubject = new Subject<void>();
    private isInitialRenderComplete = false;
    private renderTimeout: any;
    private hasEmittedCompletion = false; // Flag to prevent multiple emissions

    // Batch size configuration for different data ranges
    private readonly BATCH_SIZES = {
        SMALL: 25,    // 11-100 layers
        MEDIUM: 50,   // 101-1000 layers
        LARGE: 100    // 1000+ layers
    };

    constructor(private rxjsService: RxjsService, private snackbarService: SnackbarService) {
        this.rxjsService.setGlobalLoaderProperty(true);
    }

    // Public methods for controlling clustering
    toggleClustering(): void {
        this.clusteringEnabled = !this.clusteringEnabled;
        if (this.clusteringEnabled) {
            this.enableClustering();
        } else {
            this.disableClustering();
        }
        
        // Get current marker count for logging
        const vehicleCount = this.vehicleClusterGroup ? this.vehicleClusterGroup.getLayers().length : 0;
        const siteCount = this.siteClusterGroup ? this.siteClusterGroup.getLayers().length : 0;
        console.log(`Clustering ${this.clusteringEnabled ? 'enabled' : 'disabled'} for ${vehicleCount + siteCount} markers`);
    }

    showIndividualMarkers(): void {
        this.clusteringEnabled = false;
        this.disableClustering();
    }

    showClustersOnly(): void {
        this.clusteringEnabled = true;
        this.enableClustering();
    }

    // Debug method to show current layer state
    showLayerState(): void {
        console.log('=== CURRENT LAYER STATE ===');
        console.log(`isSignalRData: ${this.isSignalRData}`);
        console.log(`clusteringEnabled: ${this.clusteringEnabled}`);
        console.log(`Polygon layers: ${this.editablePolygonLayers.getLayers().length}`);
        console.log(`Polyline layers: ${this.editablePolyLineLayers.getLayers().length}`);
        console.log(`Vehicle markers: ${this.vehicleClusterGroup.getLayers().length}`);
        console.log(`Site markers: ${this.siteClusterGroup.getLayers().length}`);
        console.log(`Cache entries: ${this.layerCache.size}`);
        
        // Show cache breakdown
        const cacheKeys = Array.from(this.layerCache.keys());
        const polygonCache = cacheKeys.filter(key => key.startsWith('polygon_'));
        const markerCache = cacheKeys.filter(key => key.startsWith('marker_'));
        const polylineCache = cacheKeys.filter(key => key.startsWith('polyline_'));
        const iconCache = cacheKeys.filter(key => key.startsWith('icon_'));
        
        console.log(`Cache breakdown:`);
        console.log(`  Polygons: ${polygonCache.length}`);
        console.log(`  Markers: ${markerCache.length}`);
        console.log(`  Polylines: ${polylineCache.length}`);
        console.log(`  Icons: ${iconCache.length}`);
        console.log('=== END LAYER STATE ===');
    }

    // Debug method to show current markers and polylines on map
    showCurrentLayers(): void {
        console.log('=== CURRENT LAYERS ON MAP ===');
        
        // Show markers in cluster groups
        if (this.vehicleClusterGroup) {
            const vehicleMarkers = this.vehicleClusterGroup.getLayers();
            console.log(`Vehicle cluster markers (${vehicleMarkers.length}):`);
            vehicleMarkers.forEach((marker: any, index: number) => {
                const id = marker.options?.id || marker._leaflet_id;
                const name = marker.options?.displayName || 'Unknown';
                console.log(`  ${index + 1}. ID: ${id}, Name: ${name}`);
            });
        }
        
        if (this.siteClusterGroup) {
            const siteMarkers = this.siteClusterGroup.getLayers();
            console.log(`Site cluster markers (${siteMarkers.length}):`);
            siteMarkers.forEach((marker: any, index: number) => {
                const id = marker.options?.id || marker._leaflet_id;
                const name = marker.options?.displayName || 'Unknown';
                console.log(`  ${index + 1}. ID: ${id}, Name: ${name}`);
            });
        }
        
        // Show polylines
        const polylines = this.editablePolyLineLayers.getLayers();
        console.log(`Polylines (${polylines.length}):`);
        polylines.forEach((polyline: any, index: number) => {
            const id = polyline.options?.id || polyline._leaflet_id;
            console.log(`  ${index + 1}. ID: ${id}`);
        });
        
        // Show layer cache
        console.log(`Layer cache entries (${this.layerCache.size}):`);
        this.layerCache.forEach((layer, key) => {
            const layerType = key.split('_')[0];
            const layerId = key.split('_')[1];
            console.log(`  ${key} (${layerType})`);
        });
        
        console.log('=== END CURRENT LAYERS ===');
    }

    // Test method to remove a specific marker by ID
    testRemoveMarker(id: string): void {
        console.log(`🧪 Testing removal of marker: ${id}`);
        this.showCurrentLayers();
        this.removeMarkerById(id);
        console.log('After removal:');
        this.showCurrentLayers();
    }

    // Test method to remove a specific polyline by ID
    testRemovePolyline(id: string): void {
        console.log(`🧪 Testing removal of polyline: ${id}`);
        this.showCurrentLayers();
        this.removePolylineById(id);
        console.log('After removal:');
        this.showCurrentLayers();
    }

    // Force refresh the map view
    forceMapRefresh(): void {
        console.log('🔄 Forcing map refresh...');
        if (this.map) {
            this.map.invalidateSize();
            this.map.fire('resize');
            console.log('✅ Map refresh triggered');
        } else {
            console.warn('⚠️ Map not initialized');
        }
    }

    // Force full map recreation (for testing)
    forceFullMapRecreation(): void {
        console.log('🔄 Manually triggering full map recreation...');
        this.isSignalRData = false; // Set to false to trigger full recreation
        this.recreateMapWithoutInitialData();
    }

    // Manual marker removal with visual feedback
    manualRemoveMarker(id: string): void {
        console.log(`🔧 Manual removal of marker: ${id}`);
        
        // Try all possible removal methods
        let removed = false;
        
        // Method 1: Remove from cache and map
        const cachedMarker = this.layerCache.get(`marker_${id}`);
        if (cachedMarker) {
            if (this.clusteringEnabled) {
                const markerOptions = (cachedMarker as any).options;
                const isVehicle = markerOptions?.legentType === 'Vehicle';
                const clusterGroup = isVehicle ? this.vehicleClusterGroup : this.siteClusterGroup;
                if (clusterGroup) {
                    clusterGroup.removeLayer(cachedMarker);
                    removed = true;
                    console.log(`✅ Removed from cluster group`);
                }
            } else {
                this.map.removeLayer(cachedMarker);
                removed = true;
                console.log(`✅ Removed from map directly`);
            }
        }
        
        // Method 2: Search all map layers (using layer cache instead)
        if (!removed) {
            // Since map.getLayers() doesn't exist, we'll rely on our layer cache
            console.log(`Checking layer cache for marker: ${id}`);
        }
        
        // Method 3: Force remove by ID from all possible locations
        if (!removed) {
            // Remove from vehicle cluster
            if (this.vehicleClusterGroup) {
                const vehicleMarkers = this.vehicleClusterGroup.getLayers();
                const vehicleMarker = vehicleMarkers.find((marker: any) => 
                    marker.options?.id === id || marker._leaflet_id === id
                );
                if (vehicleMarker) {
                    this.vehicleClusterGroup.removeLayer(vehicleMarker);
                    removed = true;
                    console.log(`✅ Removed from vehicle cluster`);
                }
            }
            
            // Remove from site cluster
            if (!removed && this.siteClusterGroup) {
                const siteMarkers = this.siteClusterGroup.getLayers();
                const siteMarker = siteMarkers.find((marker: any) => 
                    marker.options?.id === id || marker._leaflet_id === id
                );
                if (siteMarker) {
                    this.siteClusterGroup.removeLayer(siteMarker);
                    removed = true;
                    console.log(`✅ Removed from site cluster`);
                }
            }
        }
        
        // Clean up cache
        this.layerCache.delete(`marker_${id}`);
        
        if (removed) {
            console.log(`✅ Successfully removed marker: ${id}`);
            this.forceMapRefresh();
        } else {
            console.warn(`⚠️ Could not find marker to remove: ${id}`);
        }
    }

    // Debug method to show SignalR update details
    debugSignalRUpdate(data: any): void {
        console.log('=== SIGNALR UPDATE DEBUG ===');
        console.log(`Data received:`, data);
        
        if (data.polyLayers) {
            const markers = data.polyLayers.filter((layer: any) => {
                const props = layer.feature?.properties || layer.options;
                return props.shapeType === 'Point' || layer.feature?.geometry?.type === 'Point';
            });
            
            const polylines = data.polyLayers.filter((layer: any) => {
                const props = layer.feature?.properties || layer.options;
                return props.shapeType === 'LineString' || layer.feature?.geometry?.type === 'LineString';
            });
            
            const polygons = data.polyLayers.filter((layer: any) => {
                const props = layer.feature?.properties || layer.options;
                return props.shapeType === 'Polygon' || layer.feature?.geometry?.type === 'Polygon';
            });
            
            console.log(`Layers breakdown:`);
            console.log(`  Markers: ${markers.length}`);
            console.log(`  Polylines: ${polylines.length}`);
            console.log(`  Polygons: ${polygons.length}`);
            
            // Analyze occurrenceBookId patterns
            const occurrenceBookIdMap = new Map<string, any[]>();
            
            [...markers, ...polylines].forEach((layer: any) => {
                const props = layer.feature?.properties || layer.options;
                const id = props.id;
                const isRemoved = props.isRemoved === true;
                
                if (id) {
                    if (!occurrenceBookIdMap.has(id)) {
                        occurrenceBookIdMap.set(id, []);
                    }
                    occurrenceBookIdMap.get(id)!.push({
                        isRemoved,
                        geometryType: props.shapeType || layer.feature?.geometry?.type,
                        displayName: props.displayName
                    });
                }
            });
            
            console.log('=== OCCURRENCE BOOK ID ANALYSIS ===');
            occurrenceBookIdMap.forEach((entries, id) => {
                if (entries.length === 1) {
                    const entry = entries[0];
                    if (entry.isRemoved) {
                        console.log(`📤 DELETE: ${id} (${entry.displayName})`);
                    } else {
                        console.log(`📥 CREATE: ${id} (${entry.displayName})`);
                    }
                } else if (entries.length === 2) {
                    const [first, second] = entries;
                    if (first.isRemoved && !second.isRemoved) {
                        console.log(`🔄 UPDATE: ${id} (${second.displayName}) - Remove old, add new`);
                    } else {
                        console.log(`⚠️  UNEXPECTED: ${id} has ${entries.length} entries`);
                    }
                } else {
                    console.log(`⚠️  MULTIPLE: ${id} has ${entries.length} entries`);
                }
            });
            
            // Check for removed items
            const removedMarkers = markers.filter((layer: any) => {
                const props = layer.feature?.properties || layer.options;
                return props.isRemoved === true;
            });
            
            const removedPolylines = polylines.filter((layer: any) => {
                const props = layer.feature?.properties || layer.options;
                return props.isRemoved === true;
            });
            
            console.log(`Removed items:`);
            console.log(`  Markers to remove: ${removedMarkers.length}`);
            console.log(`  Polylines to remove: ${removedPolylines.length}`);
            
            if (removedMarkers.length > 0) {
                console.log('Markers to remove:', removedMarkers.map((layer: any) => {
                    const props = layer.feature?.properties || layer.options;
                    return { id: props.id, name: props.displayName };
                }));
            }
        }
        console.log('=== END SIGNALR DEBUG ===');
    }

    // Method to configure batch sizes dynamically
    configureBatchSizes(small: number, medium: number, large: number): void {
        this.BATCH_SIZES.SMALL = small;
        this.BATCH_SIZES.MEDIUM = medium;
        this.BATCH_SIZES.LARGE = large;
        console.log('Batch sizes updated:', this.BATCH_SIZES);
    }

    // Method to show current batch configuration
    showBatchConfiguration(): void {
        console.log('=== BATCH CONFIGURATION ===');
        console.log('Current batch sizes:', this.BATCH_SIZES);
        console.log('Render delay:', this.RENDER_DELAY, 'ms');
        console.log('Performance thresholds:');
        console.log(`  ≤10 layers: Immediate rendering`);
        console.log(`  11-100 layers: Batch size ${this.BATCH_SIZES.SMALL}`);
        console.log(`  101-1000 layers: Batch size ${this.BATCH_SIZES.MEDIUM}`);
        console.log(`  1000+ layers: Batch size ${this.BATCH_SIZES.LARGE}`);
        console.log('=== END BATCH CONFIGURATION ===');
    }

    // Rendering synchronization methods
    private startRenderOperation(): void {
        this.pendingRenderOperations++;
        this.hasEmittedCompletion = false; // Reset emission flag for new rendering cycle
        console.log(`🔄 Render operation started. Pending: ${this.pendingRenderOperations}`);
        
        // Clear any existing timeout
        if (this.renderTimeout) {
            clearTimeout(this.renderTimeout);
        }
    }

    private completeRenderOperation(): void {
        this.pendingRenderOperations--;
        console.log(`✅ Render operation completed. Pending: ${this.pendingRenderOperations}`);
        
        if (this.pendingRenderOperations <= 0) {
            this.pendingRenderOperations = 0; // Ensure it doesn't go negative
            this.scheduleRenderComplete();
        }
    }

    private scheduleRenderComplete(): void {
        // Clear any existing timeout
        if (this.renderTimeout) {
            clearTimeout(this.renderTimeout);
        }
        
        // Schedule the completion with a small delay to ensure all operations are truly complete
        this.renderTimeout = setTimeout(() => {
            this.emitRenderComplete();
        }, 100); // 100ms delay to ensure all async operations complete
    }

    private emitRenderComplete(): void {
        // Prevent multiple emissions in the same rendering cycle
        if (this.hasEmittedCompletion) {
            console.log('⚠️ Completion already emitted, skipping duplicate emission');
            return;
        }
        
        this.hasEmittedCompletion = true; // Mark as emitted
        console.log('🎯 All render operations completed. Emitting afterDataRenderedInMapAction');
        
        // Ensure map is properly updated
        if (this.map) {
            this.map.invalidateSize();
            // Small delay to ensure DOM updates are complete
            setTimeout(() => {
                this.map.fire('resize');
                this.afterDataRenderedInMapAction.emit(true);
                this.renderCompletionSubject.next();
                this.isInitialRenderComplete = true;
            }, 50);
        } else {
            this.afterDataRenderedInMapAction.emit(true);
            this.renderCompletionSubject.next();
            this.isInitialRenderComplete = true;
        }
    }

    // Method to check if rendering is complete
    isRenderingComplete(): boolean {
        return this.pendingRenderOperations === 0 && this.isInitialRenderComplete;
    }

    // Method to get current rendering status
    getRenderingStatus(): { pending: number; isComplete: boolean; isInitialComplete: boolean } {
        return {
            pending: this.pendingRenderOperations,
            isComplete: this.pendingRenderOperations === 0,
            isInitialComplete: this.isInitialRenderComplete
        };
    }

    // Method to wait for rendering completion
    waitForRenderingComplete(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.pendingRenderOperations === 0 && this.isInitialRenderComplete) {
                resolve(true);
                return;
            }

            const subscription = this.renderCompletionSubject.pipe(
                takeUntil(this.destroy$)
            ).subscribe(() => {
                subscription.unsubscribe();
                resolve(true);
            });

            // Fallback timeout
            setTimeout(() => {
                subscription.unsubscribe();
                resolve(false);
            }, 30000); // 30 second timeout
        });
    }

    // Method to force completion (for edge cases)
    forceRenderingComplete(): void {
        console.log('⚠️ Force completing rendering operations');
        this.pendingRenderOperations = 0;
        this.hasEmittedCompletion = false; // Reset flag to allow emission
        this.scheduleRenderComplete();
    }

    // Method to manually trigger completion (for debugging/testing)
    manualTriggerCompletion(): void {
        console.log('🔧 Manually triggering completion');
        this.hasEmittedCompletion = false; // Reset flag to allow emission
        this.emitRenderComplete();
    }

    // Method to check if there are any pending operations
    hasPendingOperations(): boolean {
        return this.pendingRenderOperations > 0;
    }

    // Method to reset emission flag for new rendering cycle
    resetEmissionFlag(): void {
        this.hasEmittedCompletion = false;
        console.log('🔄 Emission flag reset for new rendering cycle');
    }

    // Debug method to show current rendering state
    debugRenderingState(): void {
        console.log('=== RENDERING STATE DEBUG ===');
        console.log(`Pending operations: ${this.pendingRenderOperations}`);
        console.log(`Initial render complete: ${this.isInitialRenderComplete}`);
        console.log(`Has emitted completion: ${this.hasEmittedCompletion}`);
        console.log(`Has timeout: ${this.renderTimeout ? 'Yes' : 'No'}`);
        console.log(`Map initialized: ${this.map ? 'Yes' : 'No'}`);
        console.log(`Layer cache size: ${this.layerCache.size}`);
        console.log(`Render queue length: ${this.renderQueue.length}`);
        console.log('=== END RENDERING STATE ===');
    }

    private enableClustering(): void {
        if (!this.vehicleClusterGroup || !this.siteClusterGroup) {
            console.warn('Cluster groups not initialized, cannot enable clustering');
            return;
        }
        
        this.map.removeLayer(this.vehicleClusterGroup);
        this.map.removeLayer(this.siteClusterGroup);
        
        const vehicleMarkers = this.vehicleClusterGroup.getLayers();
        const siteMarkers = this.siteClusterGroup.getLayers();
        
        vehicleMarkers.forEach((marker: any) => this.map.removeLayer(marker));
        siteMarkers.forEach((marker: any) => this.map.removeLayer(marker));
        
        this.map.addLayer(this.vehicleClusterGroup);
        this.map.addLayer(this.siteClusterGroup);
    }

    private disableClustering(): void {
        if (!this.vehicleClusterGroup || !this.siteClusterGroup) {
            console.warn('Cluster groups not initialized, cannot disable clustering');
            return;
        }
        
        this.map.removeLayer(this.vehicleClusterGroup);
        this.map.removeLayer(this.siteClusterGroup);
        
        const vehicleMarkers = this.vehicleClusterGroup.getLayers();
        const siteMarkers = this.siteClusterGroup.getLayers();
        
        vehicleMarkers.forEach((marker: any) => this.map.addLayer(marker));
        siteMarkers.forEach((marker: any) => this.map.addLayer(marker));
    }

    ngOnInit(): void {
        this.initializeMap();
        this.setupDataUpdateStream();
    }

    private setupDataUpdateStream(): void {
        // Debounce SignalR updates for smooth performance
        this.dataUpdateSubject.pipe(
            debounceTime(50), // Reduced for more responsive updates
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.processRenderQueue();
        });
    }

    ngAfterViewInit() {
        requestAnimationFrame(() => {
            this.initializeClusterGroups();
            
            // Only add cluster groups if they were successfully initialized
            if (this.vehicleClusterGroup && this.siteClusterGroup) {
                this.map.addLayer(this.vehicleClusterGroup);
                this.map.addLayer(this.siteClusterGroup);
            } else {
                console.error('Failed to initialize cluster groups');
            }
            
            this.map.addLayer(this.editablePolygonLayers);
            this.map.addLayer(this.editablePolyLineLayers);
            
            // Add zoom event listener with minimal performance optimization
            this.map.on('zoomend', () => this.onZoomChange());
            
            // Create draw control
            this.drawControl = new L.Control.Draw({
                draw: {
                    circle: false,
                    circlemarker: false,
                    marker: false,
                    polygon: false,
                    polyline: false,
                    rectangle: false
                }
            });
            this.map.addControl(this.drawControl);
        });
    }

    private onZoomChange(): void {
        const currentZoom = this.map.getZoom();
        const disableClusteringAtZoom = this.siteClusterGroup.options.disableClusteringAtZoom;
        
        if (this.clusteringEnabled) {
            console.log(`Zoom: ${currentZoom}, Clustering: ${currentZoom >= disableClusteringAtZoom ? 'Disabled' : 'Enabled'}`);
        }
        
        // Minimal zoom performance optimization - force a small delay to prevent double zoom
        setTimeout(() => {
            if (this.map) {
                this.map.invalidateSize();
            }
        }, 10);
    }

    private initializeClusterGroups(): void {
        this.vehicleClusterGroup = L.markerClusterGroup({
            chunkedLoading: true, // Enable for large datasets
            chunkInterval: 100,
            chunkDelay: 50,
            maxClusterRadius: 80,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            disableClusteringAtZoom: 18,
            animate: true,
            animateAddingMarkers: true,
            iconCreateFunction: (cluster: any) => {
                const count = cluster.getChildCount();
                let className = 'marker-cluster-';
                if (count < 10) {
                    className += 'small';
                } else if (count < 100) {
                    className += 'medium';
                } else {
                    className += 'large';
                }
                return L.divIcon({
                    html: `<div><span>${count}</span></div>`,
                    className: `marker-cluster ${className}`,
                    iconSize: L.point(40, 40)
                });
            }
        });

        this.siteClusterGroup = L.markerClusterGroup({
            chunkedLoading: true,
            chunkInterval: 100,
            chunkDelay: 50,
            maxClusterRadius: 80,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            disableClusteringAtZoom: 18,
            animate: true,
            animateAddingMarkers: true,
            iconCreateFunction: (cluster: any) => {
                const count = cluster.getChildCount();
                let className = 'marker-cluster-';
                if (count < 10) {
                    className += 'small';
                } else if (count < 100) {
                    className += 'medium';
                } else {
                    className += 'large';
                }
                return L.divIcon({
                    html: `<div><span>${count}</span></div>`,
                    className: `marker-cluster ${className}`,
                    iconSize: L.point(40, 40)
                });
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        for (let prop in changes) {
            switch (prop) {
                case 'polyLayersObject':
                    this.handlePolyLayersObjectChange();
                    break;
                case "zoomInOptions":
                    this.handleZoomInOptionsChange();
                    break;
                case "zoomInOptionsForDispatcherArea":
                    this.handleZoomInOptionsForDispatcherAreaChange();
                    break;
                case "targetLatLong":
                    this.handleTargetLatLongChange();
                    break;
                case "isSignalRData":
                    this.handleIsSignalRDataChange();
                    break;
            }
        }
    }

    private handlePolyLayersObjectChange(): void {
        if (!this.map || !this.polyLayersObject) return;
        this.queueDataForRendering();
    }

    private queueDataForRendering(): void {
        this.renderQueue.push({ ...this.polyLayersObject });
        this.dataUpdateSubject.next();
    }

    private processRenderQueue(): void {
        if (this.isRendering || this.renderQueue.length === 0) return;

        this.isRendering = true;
        const dataToRender = this.renderQueue.pop();
        this.renderQueue = []; // Keep only latest

        this.renderMapDataOptimized(dataToRender);
        this.isRendering = false;
    }

    private renderMapDataOptimized(dataToRender: IpolyLayersObject): void {
        if (!dataToRender || !dataToRender.polyLayers) return;

        const layerCount = dataToRender.polyLayers.length;
        console.log(`Rendering ${layerCount} layers, isSignalRData: ${this.isSignalRData}`);

        // Start render operation
        this.startRenderOperation();

        // Handle different scenarios based on isSignalRData flag
        if (!this.isSignalRData) {
            // Full refresh: clear everything and recreate
            this.cleanupLayersOptimized();
            this.renderAllLayers(dataToRender.polyLayers);
        } else {
            // SignalR update: smart update existing layers
            this.updateLayersForSignalR(dataToRender.polyLayers);
        }
    }

    private renderAllLayers(layers: any[]): void {
        const layerCount = layers.length;
        console.log(`Rendering ${layerCount} layers with batch processing`);
        
        // Optimized rendering based on data size using configured batch sizes
        if (layerCount <= 10) {
            console.log('Using immediate rendering for small dataset');
            this.renderLayersImmediately(layers);
        } else if (layerCount <= 100) {
            console.log(`Using batch processing with size ${this.BATCH_SIZES.SMALL}`);
            this.processLayersInBatches(layers, this.BATCH_SIZES.SMALL);
        } else if (layerCount <= 1000) {
            console.log(`Using batch processing with size ${this.BATCH_SIZES.MEDIUM}`);
            this.processLayersInBatches(layers, this.BATCH_SIZES.MEDIUM);
        } else {
            console.log(`Using batch processing with size ${this.BATCH_SIZES.LARGE} for large dataset`);
            this.processLayersInBatches(layers, this.BATCH_SIZES.LARGE);
        }
    }

    private updateLayersForSignalR(layers: any[]): void {
        console.log('Processing SignalR update with smart layer management');
        
        // Check if cluster groups are initialized
        if (!this.vehicleClusterGroup || !this.siteClusterGroup) {
            console.warn('Cluster groups not initialized, falling back to full refresh');
            this.cleanupLayersOptimized();
            this.renderAllLayers(layers);
            return;
        }
        
        // Get existing layers for comparison
        const existingMarkers = new Map<string, any>();
        const existingPolylines = new Map<string, any>();
        
        // Collect existing markers from cluster groups (for clustering mode)
        this.vehicleClusterGroup.getLayers().forEach((marker: any) => {
            const id = marker.options?.id || marker._leaflet_id;
            if (id) {
                existingMarkers.set(id, marker);
                console.log(`Found existing vehicle marker: ${id}`);
            }
        });
        
        this.siteClusterGroup.getLayers().forEach((marker: any) => {
            const id = marker.options?.id || marker._leaflet_id;
            if (id) {
                existingMarkers.set(id, marker);
                console.log(`Found existing site marker: ${id}`);
            }
        });
        
        // Collect existing markers from layer cache (for non-clustering mode)
        this.layerCache.forEach((layer, key) => {
            if (key.startsWith('marker_')) {
                const id = key.replace('marker_', '');
                if (!existingMarkers.has(id)) {
                    existingMarkers.set(id, layer);
                    //console.log(`Found existing marker from cache: ${id}`);
                }
            }
        });
        
        // Collect existing polylines
        this.editablePolyLineLayers.getLayers().forEach((polyline: any) => {
            const id = polyline.options?.id || polyline._leaflet_id;
            if (id) {
                existingPolylines.set(id, polyline);
                //console.log(`Found existing polyline: ${id}`);
            }
        });
        
        //console.log(`Found ${existingMarkers.size} existing markers and ${existingPolylines.size} existing polylines`);
        
        // Also check layer cache
        console.log(`Layer cache has ${this.layerCache.size} entries:`);
        this.layerCache.forEach((layer, key) => {
            if (key.startsWith('marker_')) {
                //console.log(`  Cache marker: ${key}`);
            }
        });
        
        // Process new data with proper handling of duplicate occurrenceBookId
        const markersToAdd = [];
        const polylinesToAdd = [];
        const markersToRemove = new Set<string>();
        const polylinesToRemove = new Set<string>();
        const processedIds = new Set<string>(); // Track processed IDs to handle duplicates
        
        // First pass: Process all layers and collect actions
        layers.forEach((layer) => {
            try {
                const props = layer.feature?.properties || layer.options;
                const geometryType = layer.feature?.geometry?.type || props.shapeType;
                const id = props.id;
                const isRemoved = props.isRemoved === true;

                if (!id) return;

                if (geometryType === 'Point') {
                    if (isRemoved) {
                        // Mark for removal - this will remove existing marker
                        markersToRemove.add(id);
                        console.log(`Marking marker ${id} for removal (isRemoved: true)`);
                    } else {
                        // Check if this ID was already processed for removal
                        if (markersToRemove.has(id)) {
                            // This is an update case: remove old, add new
                            console.log(`Processing update for marker ${id}: removing old, adding new`);
                            markersToRemove.delete(id); // Don't remove, we'll replace
                        }
                        
                        // Always add to creation list (will replace if exists)
                        markersToAdd.push({ layer, props, id });
                        console.log(`Adding marker ${id} to creation list (isRemoved: false)`);
                    }
                } else if (geometryType === 'LineString') {
                    if (isRemoved) {
                        // Mark for removal - this will remove existing polyline
                        polylinesToRemove.add(id);
                        console.log(`Marking polyline ${id} for removal (isRemoved: true)`);
                    } else {
                        // Check if this ID was already processed for removal
                        if (polylinesToRemove.has(id)) {
                            // This is an update case: remove old, add new
                            console.log(`Processing update for polyline ${id}: removing old, adding new`);
                            polylinesToRemove.delete(id); // Don't remove, we'll replace
                        }
                        
                        // Always add to creation list (will replace if exists)
                        polylinesToAdd.push({ layer, props, id });
                        console.log(`Adding polyline ${id} to creation list (isRemoved: false)`);
                    }
                } else if (geometryType === 'Polygon') {
                    // Polygons are static, only add if they don't exist
                    const polygonKey = `polygon_${id}`;
                    if (!this.layerCache.has(polygonKey)) {
                        this.addPolygonLayer(layer, id, props);
                    }
                }
            } catch (error) {
                console.error('Error processing layer for SignalR update:', error);
            }
        });
        
        console.log(`Processing summary:`);
        console.log(`  Markers to remove: ${markersToRemove.size}`);
        console.log(`  Markers to add: ${markersToAdd.length}`);
        console.log(`  Polylines to remove: ${polylinesToRemove.size}`);
        console.log(`  Polylines to add: ${polylinesToAdd.length}`);
        
        // Second pass: Execute removals first
        console.log('Executing removals...');
        markersToRemove.forEach(id => {
            this.removeMarkerById(id);
        });
        
        polylinesToRemove.forEach(id => {
            this.removePolylineById(id);
        });
        
        // Third pass: Execute additions (this will replace if exists)
        console.log('Executing additions...');
        markersToAdd.forEach(({ props, id }) => {
            // Remove existing marker first if it exists (for updates)
            if (existingMarkers.has(id)) {
                console.log(`Removing existing marker ${id} before adding updated version`);
                this.removeMarkerById(id);
            }
            this.addPointLayerToMapOptimized(props, id);
        });
        
        polylinesToAdd.forEach(({ props, id }) => {
            // Remove existing polyline first if it exists (for updates)
            if (existingPolylines.has(id)) {
                console.log(`Removing existing polyline ${id} before adding updated version`);
                this.removePolylineById(id);
            }
            this.addPolylineLayerOptimized(props, id);
        });
        
        console.log(`✅ SignalR update completed:`);
        console.log(`  Markers: ${markersToAdd.length} added, ${markersToRemove.size} removed`);
        console.log(`  Polylines: ${polylinesToAdd.length} added, ${polylinesToRemove.size} removed`);
        
        // Force map refresh to ensure visual updates
        if (this.map) {
            this.map.invalidateSize();
            // Trigger a small delay to ensure DOM updates
            setTimeout(() => {
                this.map.fire('resize');
            }, 10);
        }
        
        // Complete render operation instead of directly emitting
        this.completeRenderOperation();
    }

    private removeMarkerById(id: string): void {
        let markerRemoved = false;
        
        // First, try to find and remove from layer cache (works for both clustering and non-clustering)
        const cachedMarker = this.layerCache.get(`marker_${id}`);
        if (cachedMarker) {
            if (this.clusteringEnabled) {
                // Remove from appropriate cluster group
                const markerOptions = (cachedMarker as any).options;
                const isVehicle = markerOptions?.legentType === 'Vehicle';
                const clusterGroup = isVehicle ? this.vehicleClusterGroup : this.siteClusterGroup;
                if (clusterGroup) {
                    clusterGroup.removeLayer(cachedMarker);
                    markerRemoved = true;
                    console.log(`Removed marker ${id} from ${isVehicle ? 'vehicle' : 'site'} cluster group`);
                }
            } else {
                // Remove directly from map
                this.map.removeLayer(cachedMarker);
                markerRemoved = true;
                console.log(`Removed marker ${id} directly from map`);
            }
        }
        
        // Fallback: search in cluster groups if not found in cache
        if (!markerRemoved && this.clusteringEnabled) {
            // Remove from vehicle cluster group
            const vehicleMarkers = this.vehicleClusterGroup.getLayers();
            const vehicleMarker = vehicleMarkers.find((marker: any) => 
                marker.options?.id === id || marker._leaflet_id === id
            );
            if (vehicleMarker) {
                this.vehicleClusterGroup.removeLayer(vehicleMarker);
                markerRemoved = true;
                console.log(`Removed marker ${id} from vehicle cluster group (fallback)`);
            }
            
            // Remove from site cluster group
            const siteMarkers = this.siteClusterGroup.getLayers();
            const siteMarker = siteMarkers.find((marker: any) => 
                marker.options?.id === id || marker._leaflet_id === id
            );
            if (siteMarker) {
                this.siteClusterGroup.removeLayer(siteMarker);
                markerRemoved = true;
                console.log(`Removed marker ${id} from site cluster group (fallback)`);
            }
        }
        
        // Remove from cache
        this.layerCache.delete(`marker_${id}`);
        
        if (markerRemoved) {
            console.log(`✅ Successfully removed marker: ${id}`);
        } else {
            console.warn(`⚠️ Could not find marker to remove: ${id}`);
        }
    }

    private removePolylineById(id: string): void {
        let polylineRemoved = false;
        
        // First, try to find and remove from layer cache
        const cachedPolyline = this.layerCache.get(`polyline_${id}`);
        if (cachedPolyline) {
            this.editablePolyLineLayers.removeLayer(cachedPolyline);
            polylineRemoved = true;
            console.log(`Removed polyline ${id} from layer cache`);
        }
        
        // Fallback: search in polyline layers
        if (!polylineRemoved) {
            const polylines = this.editablePolyLineLayers.getLayers();
            const polyline = polylines.find((line: any) => 
                line.options?.id === id || line._leaflet_id === id
            );
            if (polyline) {
                this.editablePolyLineLayers.removeLayer(polyline);
                polylineRemoved = true;
                console.log(`Removed polyline ${id} from layer group (fallback)`);
            }
        }
        
        // Remove from cache
        this.layerCache.delete(`polyline_${id}`);
        
        if (polylineRemoved) {
            console.log(`✅ Successfully removed polyline: ${id}`);
        } else {
            console.warn(`⚠️ Could not find polyline to remove: ${id}`);
        }
    }

    private extractCoordinates(coordinates: any): [number, number] | null {
        if (Array.isArray(coordinates) && coordinates.length >= 2) {
            return [coordinates[0], coordinates[1]] as [number, number];
        } else if (coordinates && typeof coordinates === 'object') {
            return [coordinates.lat || coordinates.latitude, coordinates.lng || coordinates.longitude] as [number, number];
        }
        return null;
    }

    private extractPolylineCoordinates(coordinates: any): L.LatLng[] | null {
        if (!coordinates) return null;
        
        try {
            if (Array.isArray(coordinates)) {
                if (coordinates.length === 0) return null;
                
                if (Array.isArray(coordinates[0])) {
                    return coordinates.map((coord: any) => {
                        if (Array.isArray(coord) && coord.length >= 2) {
                            return new L.LatLng(coord[0], coord[1]);
                        }
                        throw new Error(`Invalid coordinate format`);
                    });
                } else if (coordinates.length >= 2) {
                    const coords = [];
                    for (let i = 0; i < coordinates.length; i += 2) {
                        if (i + 1 < coordinates.length) {
                            coords.push(new L.LatLng(coordinates[i], coordinates[i + 1]));
                        }
                    }
                    return coords;
                }
            } else if (coordinates && typeof coordinates === 'object') {
                if (coordinates.type === 'LineString' && Array.isArray(coordinates.coordinates)) {
                    return coordinates.coordinates.map((coord: any) => {
                        if (Array.isArray(coord) && coord.length >= 2) {
                            return new L.LatLng(coord[1], coord[0]);
                        }
                        throw new Error(`Invalid GeoJSON coordinate`);
                    });
                }
            }
        } catch (error) {
            console.error('Error extracting polyline coordinates:', error);
        }
        return null;
    }
    private cleanupLayersOptimized(): void {
        console.log('🧹 Starting comprehensive layer cleanup...');
        
        // Clear cluster groups
        if (this.vehicleClusterGroup) {
            this.vehicleClusterGroup.clearLayers();
            console.log('✅ Cleared vehicle cluster group');
        }
        if (this.siteClusterGroup) {
            this.siteClusterGroup.clearLayers();
            console.log('✅ Cleared site cluster group');
        }
        
        // Clear feature groups
        this.editablePolyLineLayers.clearLayers();
        this.editablePolygonLayers.clearLayers();
        this.currentLocationAndDestinationMarkerSiteLayers.clearLayers();
        console.log('✅ Cleared feature groups');
        
        // Remove markers that were added directly to the map (when clustering is disabled)
        if (this.map && !this.clusteringEnabled) {
            console.log('🧹 Removing markers added directly to map...');
            let removedCount = 0;
            
            // Use layer cache to find and remove direct markers
            this.layerCache.forEach((layer, key) => {
                if (key.startsWith('marker_')) {
                    try {
                        this.map.removeLayer(layer);
                        removedCount++;
                        //console.log(`✅ Removed direct marker: ${key}`);
                    } catch (error) {
                        console.warn(`⚠️ Error removing marker ${key}:`, error);
                    }
                }
            });
            
            console.log(`✅ Removed ${removedCount} direct markers from map`);
        }
        
        // Clear layer cache
        const cacheSize = this.layerCache.size;
        this.layerCache.clear();
        console.log(`✅ Cleared layer cache (${cacheSize} entries)`);
        
        console.log('✅ Comprehensive layer cleanup completed');
    }

    private renderLayersImmediately(layers: any[]): void {
        layers.forEach((layer, index) => {
            try {
                const props = layer.feature?.properties || layer.options;
                const geometryType = layer.feature?.geometry?.type || props.shapeType;
                const id = props.id;

                if (!id) return;

                if (geometryType === 'Polygon') {
                    this.addPolygonLayer(layer, id, props);
                } else if (geometryType === 'Point') {
                    this.addPointLayerToMapOptimized(props, id);
                } else if (geometryType === 'LineString') {
                    this.addPolylineLayerOptimized(props, id);
                }
            } catch (error) {
                console.error(`Error processing layer ${index + 1}:`, error);
            }
        });

        // Complete render operation instead of directly emitting
        this.completeRenderOperation();
    }

    private processLayersInBatches(layers: any[], batchSize: number): void {
        const startTime = performance.now();
        let currentIndex = 0;
        let processedCount = 0;
        let batchNumber = 0;
        const totalBatches = Math.ceil(layers.length / batchSize);

        console.log(`Starting batch processing: ${layers.length} layers in ${totalBatches} batches of ${batchSize}`);

        const processBatch = () => {
            const batchStartTime = performance.now();
            batchNumber++;
            const batch = layers.slice(currentIndex, currentIndex + batchSize);
            
            console.log(`Processing batch ${batchNumber}/${totalBatches}: ${batch.length} layers`);
            
            batch.forEach((layer, batchIndex) => {
                try {
                    const props = layer.feature?.properties || layer.options;
                    const geometryType = layer.feature?.geometry?.type || props.shapeType;
                    const id = props.id;

                    if (!id) return;

                    if (geometryType === 'Polygon') {
                        this.addPolygonLayer(layer, id, props);
                    } else if (geometryType === 'Point') {
                        this.addPointLayerToMapOptimized(props, id);
                    } else if (geometryType === 'LineString') {
                        this.addPolylineLayerOptimized(props, id);
                    }
                    
                    processedCount++;
                } catch (error) {
                    console.error(`Error processing layer ${currentIndex + batchIndex + 1}:`, error);
                }
            });

            const batchEndTime = performance.now();
            const batchDuration = batchEndTime - batchStartTime;
            console.log(`Batch ${batchNumber} completed in ${batchDuration.toFixed(2)}ms`);

            currentIndex += batchSize;

            if (currentIndex < layers.length) {
                setTimeout(processBatch, this.RENDER_DELAY);
            } else {
                const totalTime = performance.now() - startTime;
                console.log(`✅ Batch processing completed:`);
                console.log(`  Total layers: ${layers.length}`);
                console.log(`  Processed: ${processedCount}`);
                console.log(`  Total time: ${totalTime.toFixed(2)}ms`);
                console.log(`  Average per layer: ${(totalTime / processedCount).toFixed(2)}ms`);
                console.log(`  Average per batch: ${(totalTime / totalBatches).toFixed(2)}ms`);
                
                // Complete render operation instead of directly emitting
                this.completeRenderOperation();
            }
        };

        processBatch();
    }

    private handleZoomInOptionsChange(): void {
        if (!this.map) return;
        const southWest = new L.LatLng(this.zoomInOptions.latitude, this.zoomInOptions.longitude);
        const northEast = new L.LatLng(this.zoomInOptions.latitude, this.zoomInOptions.longitude);
        const bounds = new L.LatLngBounds(southWest, northEast);
        this.map.fitBounds(bounds);
    }

    private handleZoomInOptionsForDispatcherAreaChange(): void {
        if (!this.map || !this.zoomInOptionsForDispatcherArea.southWestLatitude || 
            this.zoomInOptionsForDispatcherArea.southWestLatitude === 0) return;

        const southWest = new L.LatLng(
            this.zoomInOptionsForDispatcherArea.southWestLatitude, 
            this.zoomInOptionsForDispatcherArea.southWestLongitude
        );
        const northEast = new L.LatLng(
            this.zoomInOptionsForDispatcherArea.northEastLatitude, 
            this.zoomInOptionsForDispatcherArea.northEastLongitude
        );
        const bounds = new L.LatLngBounds(southWest, northEast);
        this.map.fitBounds(bounds);
    }

    private handleTargetLatLongChange(): void {
        if (this.caption !== 'Third Party Map View') return;
        this.currentLocationAndDestinationMarkerSiteLayers.clearLayers();
        if (this.targetLatLong?.latitude && this.targetLatLong?.longitude) {
            this.trackTheCurrentSourceMovingLocation();
        }
    }

    private handleIsSignalRDataChange(): void {
        console.log(`🔄 isSignalRData changed to: ${this.isSignalRData}`);
        
        if (this.isSignalRData === false) {
            console.log('🔄 Triggering full map recreation (isSignalRData = false)');
            this.recreateMapWithoutInitialData();
        } else {
            console.log('🔄 SignalR mode enabled (isSignalRData = true)');
        }
    }

    private initializeMap(): void {
        requestAnimationFrame(() => {
            if (this.map) {
                this.map.remove();
            }
            this.container = document.getElementById(this.id);
            this.map = L.map(this.container).setView(
                [this.zoomInOptions.latitude, this.zoomInOptions.longitude],
                defaultLeafLetMapZoomValue
            );
            let customPaidTileLayer = getTileLayer();
            let googleSatelliteViewTileLayer = getSatelliteTileLayer();
            
            let baseMaps: L.Control.LayersObject = {
                "Normal View": customPaidTileLayer,
                "Satellite View": googleSatelliteViewTileLayer
            };
            L.control.layers(baseMaps, null, { position: 'bottomleft' }).addTo(this.map);
            
            if (this.caption == 'Third Party Map View') {
                this.trackTheCurrentSourceMovingLocation();
                this.map.addLayer(this.currentLocationAndDestinationMarkerSiteLayers);
            }
            customPaidTileLayer.addTo(this.map);
        });
    }

    private recreateMapWithoutInitialData(): void {
        console.log('🔄 Starting full map recreation...');
        
        // Reset rendering state
        this.pendingRenderOperations = 0;
        this.isInitialRenderComplete = false;
        this.hasEmittedCompletion = false; // Reset emission flag
        
        // Clear all existing data
        this.cleanupLayersOptimized();
        console.log('✅ Cleared all layers');
        
        // Reinitialize map
        this.initializeMap();
        console.log('✅ Map reinitialized');
        
        // Reinitialize cluster groups and other components
        this.ngAfterViewInit();
        console.log('✅ AfterViewInit completed');
        
        // Queue data for rendering if available
        if (this.polyLayersObject && this.polyLayersObject.polyLayers) {
            console.log(`🔄 Queuing ${this.polyLayersObject.polyLayers.length} layers for rendering`);
            setTimeout(() => {
                this.queueDataForRendering();
            }, 100);
        } else {
            console.log('⚠️ No polyLayersObject data available for rendering');
            // If no data to render, still emit completion
            this.completeRenderOperation();
        }
        
        console.log('✅ Full map recreation completed');
    }

    private addPolygonLayer(layer: L.Layer, id: string, props: any): void {
        this.setupPolygonEvents(layer, props);
        this.editablePolygonLayers.addLayer(layer);
        this.layerCache.set(`polygon_${id}`, layer); // Use consistent prefix for polygons
    }

    private setupPolygonEvents(layer: L.Layer, props: any): void {
        const tooltip = props.displayName;
        // if (tooltip && this.polyLayersObject?.isShowLayerLabelName) {
        //     layer.bindTooltip(tooltip, {
        //         permanent: true,
        //         direction: "center",
        //         className: 'toolTipTransparentColor'
        //     }).openTooltip();
        // }

        const overStyle = { weight: 6, fillOpacity: 0.3 };
        const outStyle = { weight: 3, fillOpacity: props.fillOpacity };

        layer.on('mouseover', (e) => (layer as L.Path).setStyle(overStyle));
        layer.on('mouseout', (e) => (layer as L.Path).setStyle(outStyle));
        layer.on('click', (e) => {
            this.openedPopup = L.popup()
                .setLatLng(e.latlng)
                .setContent(`<strong>Area Name : ${props.displayName}</strong>`)
                .openOn(this.map);
        });

        (layer as L.Path).setStyle({
            color: props.color,
            fillColor: props.fillColor,
            fillOpacity: props.fillOpacity,
            weight: props.weight
        });
    }

    private addPointLayerToMapOptimized(props: any, id: string): void {
        const isVehicle = props.legentType === 'Vehicle';
        const clusterGroup = isVehicle ? this.vehicleClusterGroup : this.siteClusterGroup;
        
        let coordinates;
        if (Array.isArray(props.coordinates)) {
            coordinates = props.coordinates;
        } else if (props.coordinates && typeof props.coordinates === 'object') {
            coordinates = [props.coordinates.lat || props.coordinates.latitude, props.coordinates.lng || props.coordinates.longitude];
        } else {
            return;
        }

        const icon = this.getCachedIcon(props.iconName);
        const marker = L.marker(coordinates, { 
            icon,
            id: id // Store ID in marker options for tracking
        });

        this.setupMarkerEventsOptimized(marker, props);
        
        if (this.clusteringEnabled) {
            clusterGroup.addLayer(marker);
            //console.log(`Added marker ${id} to ${isVehicle ? 'vehicle' : 'site'} cluster group`);
        } else {
            this.map.addLayer(marker);
            //.log(`Added marker ${id} directly to map`);
        }
        
        this.layerCache.set(`marker_${id}`, marker);
        //console.log(`Cached marker ${id} in layer cache`);
    }

    private getCachedIcon(iconName: string): L.Icon {
        const cacheKey = `icon_${iconName}`;
        if (!this.layerCache.has(cacheKey)) {
            const icon = new L.Icon({ iconUrl: `${PREFIX_IMAGE_RELATIVE_PATH}${iconName}` });
            this.layerCache.set(cacheKey, icon);
        }
        return this.layerCache.get(cacheKey) as L.Icon;
    }

    private setupMarkerEventsOptimized(marker: L.Marker, props: any): void {
        // Clean up any existing tooltip first
        if (marker.getTooltip()) {
            marker.closeTooltip();
            marker.unbindTooltip();
        }

        marker.on('click', () => this.onOpenPopup.emit(props));

        marker.on('mouseover', (e) => {
            const popupHtml = this.getMarkerPopupHtml(props);
            this.openedPopup = L.popup().setLatLng(e.latlng).setContent(popupHtml).openOn(this.map);
            
            // Auto-close popup after 10 seconds
            setTimeout(() => {
                if (this.openedPopup && this.map) {
                    this.map.closePopup(this.openedPopup);
                    this.openedPopup = null;
                }
            }, 10000); // 10 seconds
        });

        marker.on('mouseout', () => {
            // Close popup immediately on mouseout
            if (this.openedPopup && this.map) {
                this.map.closePopup(this.openedPopup);
                this.openedPopup = null;
            }
        });
    }

    private getTooltipClassName(alarmCaptionPriority: number): string {
        switch (alarmCaptionPriority) {
            case AlarmCaptionPriority.RED:
            case AlarmCaptionPriority.BLUE:
            case AlarmCaptionPriority.YELLOW:
                return "toolTipBackgroundWhiteWithDisplayColor";
            default:
                return "toolTipBackgroundWhiteWithoutDisplayColor";
        }
    }

    private getMarkerPopupHtml(props: any): string {
        if (props.legentType === 'Site') {
            return `<strong>Customer Name: ${props.displayName}</strong><br>
                    <strong>Address: ${props.fullAddress || '-'}</strong><br>
                    <strong>Mobile: ${props.mobileNumber || '-'}</strong>`;
        } else {
            return `<strong>Response Officer: ${props.displayName}</strong><br>
                    <strong>Sub Area: ${props.subArea || '-'}</strong><br>
                    <strong>Vehicle Reg: ${props.vehicleRegistrationNumber || '-'}</strong><br>
                    <strong>Coordinates: ${props.coordinates || '-'}</strong>`;
        }
    }

    private addPolylineLayerOptimized(props: any, id: string): void {
        let coordinates = props.coordinates;
        
        if (!coordinates) return;

        let coords: L.LatLng[];
        
        try {
            if (Array.isArray(coordinates)) {
                if (coordinates.length === 0) return;
                
                if (Array.isArray(coordinates[0])) {
                    coords = coordinates.map((coord: any) => {
                        if (Array.isArray(coord) && coord.length >= 2) {
                            return new L.LatLng(coord[0], coord[1]);
                        } else {
                            throw new Error(`Invalid coordinate format`);
                        }
                    });
                } else if (coordinates.length >= 2) {
                    coords = [];
                    for (let i = 0; i < coordinates.length; i += 2) {
                        if (i + 1 < coordinates.length) {
                            const latLng = new L.LatLng(coordinates[i], coordinates[i + 1]);
                            coords.push(latLng);
                        }
                    }
                } else {
                    throw new Error(`Invalid coordinates array length`);
                }
            } else if (coordinates && typeof coordinates === 'object') {
                if (coordinates.type === 'LineString' && Array.isArray(coordinates.coordinates)) {
                    coords = coordinates.coordinates.map((coord: any) => {
                        if (Array.isArray(coord) && coord.length >= 2) {
                            return new L.LatLng(coord[1], coord[0]);
                        } else {
                            throw new Error(`Invalid GeoJSON coordinate`);
                        }
                    });
                } else {
                    throw new Error(`Unsupported coordinates object format`);
                }
            } else {
                throw new Error(`Unsupported coordinates format`);
            }

            if (coords.length < 2) return;

            const polyline = new L.Polyline(coords, {
                color: props.color || 'orange',
                weight: props.weight || 3,
                opacity: props.opacity || 1,
                id: id // Store ID in polyline options for tracking
            });

            this.setupPolylineEvents(polyline, props);
            this.editablePolyLineLayers.addLayer(polyline);
            this.layerCache.set(`polyline_${id}`, polyline);

        } catch (error) {
            console.error(`Error creating polyline for ${id}:`, error);
        }
    }

    private setupPolylineEvents(polyline: L.Polyline, props: any): void {
        polyline.on('mouseover', (e) => polyline.setStyle({ weight: 6 }));
        polyline.on('mouseout', (e) => polyline.setStyle({ weight: 3 }));
    }

    trackTheCurrentSourceMovingLocation(): void {
        this.map.locate().on('locationfound', (e) => {
            this.sourceLatLong = e.latlng;
            this.trackTheTargetMovingLocation();
        }).on('locationerror', (e) => {
            this.snackbarService.openSnackbar("Location access is denied", ResponseMessageTypes.WARNING);
        });
    }

    createIcon(iconName: string): L.Icon {
        return new L.Icon({ iconUrl: `${PREFIX_IMAGE_RELATIVE_PATH}${iconName}` });
    }

    trackTheTargetMovingLocation(): void {
        let sourceMarker = L.marker(this.sourceLatLong,
            { icon: this.createIcon("vehicle-dispatched.png") });
        this.currentLocationAndDestinationMarkerSiteLayers.addLayer(sourceMarker);
        
        if (this.targetLatLong.targetInitialLatitude && this.targetLatLong.targetInitialLongitude) {
            let targetInitialMarker = L.marker([this.targetLatLong.targetInitialLatitude, this.targetLatLong.targetInitialLongitude],
                { icon: this.createIcon("site-panic-initial.png") }).bindTooltip("Panic signal where triggered initially..!!");
            this.currentLocationAndDestinationMarkerSiteLayers.addLayer(targetInitialMarker);
            
            let currentTargetMarker = L.marker([this.targetLatLong.latitude, this.targetLatLong.longitude],
                { icon: this.createIcon("site-panic-follow.png") });
            this.currentLocationAndDestinationMarkerSiteLayers.addLayer(currentTargetMarker);
            
            let endingPoint = new L.LatLng(this.targetLatLong.latitude, this.targetLatLong.longitude);
            let latlngs = [this.sourceLatLong, endingPoint];
            let polyLine = new L.Polyline(latlngs, {
                color: '#115943',
                weight: 3
            });
            this.currentLocationAndDestinationMarkerSiteLayers.addLayer(polyLine);
            
            // Complete render operation instead of directly emitting
            this.completeRenderOperation();
        }
    }

    createRoutingPathBetweenSourceAndTarget(): void {
        if (this.map && this.targetLatLong.latitude && this.targetLatLong.longitude) {
            let waypoints = [this.sourceLatLong, [this.targetLatLong.latitude, this.targetLatLong.longitude]];
            let routingControlOptions = {
                createMarker: (i: number, waypoint: any, n: number) => {
                    let iconUrl = i == 0 ? `${PREFIX_IMAGE_RELATIVE_PATH}` + "vehicle-dispatched.png" : `${PREFIX_IMAGE_RELATIVE_PATH}` + "site-panic-follow.png";
                    const marker = L.marker(waypoint.latLng, {
                        icon: L.icon({ iconUrl })
                    });
                    return marker;
                },
                routeWhileDragging: false, draggableWaypoints: false, reverseWaypoints: false,
                addWaypoints: false, waypoints,
                lineOptions: { styles: [{ color: '#20c997', weight: 9 }] }
            };
            if (this.routingPathControl) {
                var targetLatLong = this.routingPathControl.getWaypoints()[1].latLng;
                var sourceLatLong = this.routingPathControl.getWaypoints()[0].latLng;
                var sourceLat = sourceLatLong.lat + 0.01;
                var sourceLong = sourceLatLong.lng + 0.01;
                var targetLat = targetLatLong.lat + 0.01;
                var targetLong = targetLatLong.lng + 0.01;
                this.routingPathControl.setWaypoints([[sourceLat, sourceLong], [this.targetLatLong.latitude, this.targetLatLong.longitude]]);
            }
            else {
                this.routingPathControl = L.Routing.control(routingControlOptions).addTo(this.map);
            }
            this.routingPathControl.on('routesfound', (e) => {
                let totalDistance = e.routes[0].summary.totalDistance;
                this.rxjsService.setAnyPropertyValue({ totalDistance });
                
                // Complete render operation instead of directly emitting
                this.completeRenderOperation();
            });
            this.routingPathControl._container.style.display = "None";
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        
        // Clean up rendering synchronization
        this.pendingRenderOperations = 0;
        this.isInitialRenderComplete = false;
        this.hasEmittedCompletion = false;
        this.renderCompletionSubject.complete();
        
        // Clear any pending timeouts
        if (this.renderTimeout) {
            clearTimeout(this.renderTimeout);
            this.renderTimeout = null;
        }
        
        if (this.map) {
            this.map.off();
            this.map.remove();
            this.cleanupLayersOptimized();
        }
        
        this.layerCache.clear();
        this.renderQueue = [];
    }


}